import type { JSX, ReactNode } from 'react';
import { Link, useLocation } from 'react-router';

import { FOOTER_COLUMNS, FOOTER_LEGAL, TRUST_BADGES } from '../features/marketing/content.js';
import { EligibilityCta } from '../features/marketing/eligibility-cta.js';

/**
 * AppShell — brand header + footer wrapper for all routes, matching
 * https://www.nourishedrx.com/dietitian-support. Renders no auth or language chrome (FFS-MNT DTC app
 * is public and English-only for V1).
 *
 * `<main>` does NOT constrain its children: the landing page uses full-bleed colour bands, so each
 * page owns its own container and padding.
 */
export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  // The marketing nav belongs to the landing page only. "Why NourishedRx" is an in-page
  // anchor (`#why-nourishedrx`) to a section that exists nowhere else, and "Get started
  // today" points at the eligibility route the member is already on — so from inside the
  // flow both are dead controls sitting on top of the form. The logo stays: it is the
  // only way back out.
  const isLanding = useLocation().pathname === '/';

  return (
    <div className="flex min-h-screen flex-col bg-[var(--cream)]">
      <header className="sticky top-0 z-10 border-b border-[var(--color-card-border)] bg-[var(--cream)]/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[70rem] items-center justify-between gap-4 px-6 py-3">
          <Link to="/" aria-label="NourishedRx home" className="flex items-center">
            <img
              src="/images/logo-nourishedrx.svg"
              alt="NourishedRx"
              className="h-7 w-auto"
              height={28}
            />
          </Link>
          {isLanding && (
            <div className="flex items-center gap-5">
              <a
                href="#why-nourishedrx"
                className="hidden text-sm font-medium text-[var(--forest)] hover:text-[var(--coral)] sm:inline"
              >
                Why NourishedRx
              </a>
              <EligibilityCta label="Get started today" size="sm" />
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[var(--color-card-border)] bg-[var(--cream)]">
        <div className="mx-auto flex w-full max-w-[70rem] flex-col gap-10 px-6 py-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-4">
              <img
                src="/images/logo-nourishedrx.svg"
                alt="NourishedRx"
                className="h-7 w-auto self-start"
                height={28}
              />
              <ul className="flex flex-wrap items-center gap-3">
                {TRUST_BADGES.map((b) => (
                  <li key={b.alt}>
                    <img
                      src={b.src}
                      alt={b.alt}
                      className="h-auto w-[4.5rem] object-contain"
                      width={72}
                    />
                  </li>
                ))}
              </ul>
            </div>
            {FOOTER_COLUMNS.map((col) => (
              <nav key={col.heading} className="flex flex-col gap-3" aria-label={col.heading}>
                <h3 className="text-sm font-bold text-[var(--coral)]">{col.heading}</h3>
                <ul className="flex flex-col gap-2">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-sm text-[var(--ink-soft)] hover:text-[var(--forest)]"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-card-border)] pt-6 text-xs text-[var(--ink-soft)]">
            <span>NourishedRx © 2026 All Rights Reserved.</span>
            <ul className="flex flex-wrap gap-4">
              {FOOTER_LEGAL.map((l) => (
                <li key={l.label}>
                  <a href={l.href} className="hover:text-[var(--forest)]">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
